// API client for the validator worker.
//
// The upload happens in three steps:
//   1. POST /upload-init — worker hands back a presigned PUT URL.
//   2. PUT  <uploadUrl>  — browser sends the file directly to R2.
//   3. POST /validate    — worker reads the object, validates, deletes it.
//
// Step 2 bypasses Cloudflare's ~100 MB request-body limit, which is why the
// flow has to take the R2 detour for large uploads.

import type { ValidationReport } from "@waypoint/validator-core";

export interface ValidateResponse {
  report: ValidationReport;
  ruleSet: { name: string; version: string };
  file: { name: string; size: number };
  /** Present only on package-check responses (/package/validate). */
  packageCheck?: { kind: string; notes: string[] };
}

interface UploadInitResponse {
  key: string;
  uploadUrl: string;
  expiresAt: string;
  filename: string;
  size: number;
}

const API_BASE =
  (import.meta.env.VITE_VALIDATOR_API as string | undefined)?.replace(/\/$/, "") ?? "/api";

export interface ValidatorVersion {
  id: string;
  tag?: string;
  timestamp?: string;
}

/** Ask the worker for its current version ID (populated by [version_metadata]). */
export async function fetchValidatorVersion(): Promise<ValidatorVersion> {
  const res = await fetch(`${API_BASE}/version`);
  if (!res.ok) throw new Error(`version: request failed with ${res.status}`);
  return (await res.json()) as ValidatorVersion;
}

// ---------- Shared report links (/report/:id) ----------

export interface SharedReportPayload {
  report: ValidateResponse["report"];
  ruleSet: ValidateResponse["ruleSet"];
  file: ValidateResponse["file"];
  sha256: string;
  durationMs: number;
}

export interface SharedReport extends SharedReportPayload {
  createdAt: string;
  expiresAt: string;
}

export interface CreateShareResponse {
  id: string;
  expiresAt: string;
}

/**
 * Persist a validation report for 7 days and return the id used in the
 * shareable URL `<origin>/report/:id`.
 */
export async function createSharedReport(
  payload: SharedReportPayload,
): Promise<CreateShareResponse> {
  const res = await fetch(`${API_BASE}/reports`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseJsonOrThrow<CreateShareResponse>(res, "share");
}

/** Fetch a shared report by id. Throws with a clear message when expired. */
export async function fetchSharedReport(id: string): Promise<SharedReport> {
  const res = await fetch(`${API_BASE}/reports/${encodeURIComponent(id)}`);
  if (res.status === 404) {
    throw new Error(
      "This shared report no longer exists. Shared links expire 7 days after they're created.",
    );
  }
  return parseJsonOrThrow<SharedReport>(res, "share-fetch");
}

export interface ValidateOptions {
  onUploadProgress?: (pct: number) => void;
  onStatus?: (label: string) => void;
}

export async function validateZip(
  file: File,
  opts: ValidateOptions = {},
): Promise<ValidateResponse> {
  return runUploadValidate(file, opts, "/upload-init", "/validate");
}

/**
 * Package-check counterpart to `validateZip`. Accepts a raw distribution file
 * (.mcaddon / .mcworld / .mctemplate / .mcpack) and validates it against the
 * package rule set (no Store Art / Marketing Art checks) via the /package/*
 * endpoints.
 */
export async function validatePackage(
  file: File,
  opts: ValidateOptions = {},
): Promise<ValidateResponse> {
  return runUploadValidate(file, opts, "/package/upload-init", "/package/validate");
}

async function runUploadValidate(
  file: File,
  opts: ValidateOptions,
  initPath: string,
  validatePath: string,
): Promise<ValidateResponse> {
  opts.onStatus?.("Preparing upload");
  const init = await initUpload(file, initPath);

  opts.onStatus?.("Uploading to storage");
  await uploadToR2(init.uploadUrl, file, opts.onUploadProgress);

  opts.onStatus?.("Validating");
  return validateUploaded(init.key, file.name, validatePath);
}

async function initUpload(file: File, initPath: string): Promise<UploadInitResponse> {
  const res = await fetch(`${API_BASE}${initPath}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ filename: file.name, size: file.size }),
  });
  return parseJsonOrThrow<UploadInitResponse>(res, "upload-init");
}

async function validateUploaded(
  key: string,
  filename: string,
  validatePath: string,
): Promise<ValidateResponse> {
  const res = await fetch(`${API_BASE}${validatePath}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ key, filename }),
  });
  return parseJsonOrThrow<ValidateResponse>(res, "validate");
}

/**
 * Browser → R2 PUT with progress reporting. We use XMLHttpRequest because
 * `fetch` in browsers still doesn't expose upload progress events.
 */
function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(
          new Error(
            `R2 upload failed with status ${xhr.status}${xhr.responseText ? `: ${truncate(xhr.responseText, 200)}` : ""}.`,
          ),
        );
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during upload.")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted.")));
    xhr.send(file);
  });
}

async function parseJsonOrThrow<T>(res: Response, label: string): Promise<T> {
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new Error(`${label}: server returned ${res.status} with a non-JSON body.`);
  }
  if (!res.ok) {
    const msg =
      (payload as { error?: string }).error ??
      `${label}: request failed with status ${res.status}.`;
    throw new Error(msg);
  }
  return payload as T;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}\u2026` : s;
}
